import React from 'react';
import Form from '../components/Form';
import { FaArrowRight, FaLinkedinIn, FaGithub, FaEnvelope, FaBrain, FaCode, FaGamepad, FaShieldAlt } from 'react-icons/fa';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';

const altChannels = [
  { code: '01', label: 'LINKEDIN', handle: '/in/motirebuma',  href: 'https://www.linkedin.com/in/motirebuma', Icon: FaLinkedinIn },
  { code: '02', label: 'GITHUB',   handle: '/motirebuma',     href: 'https://github.com/motirebuma',          Icon: FaGithub },
  { code: '03', label: 'EMAIL',    handle: 'moti.online7@gmail.com', href: 'mailto:moti.online7@gmail.com',                 Icon: FaEnvelope },
];

// What visitors can reach out about — same order as the rest of the site
const services = [
  { key: 'ai',        Icon: FaBrain },
  { key: 'fullstack', Icon: FaCode },
  { key: 'gamedev',   Icon: FaGamepad },
  { key: 'security',  Icon: FaShieldAlt },
];

const Contact: React.FC = () => {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const isModern = theme === 'modern';
  const year = new Date().getFullYear();

  if (isModern) {
    return (
      <section id='contact' className="modern-section relative overflow-hidden pt-24 sm:pt-28 lg:pt-32 pb-12 sm:pb-16 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto w-full">
          <article className="sf-comm">
            <div className="sf-comm__strip">
              <span className="sf-comm__tag">TRANSMISSION_LINK</span>
              <span className="sf-comm__sep">▌</span>
              <span className="sf-comm__channel">CHANNEL: OPEN</span>
              <span className="sf-comm__sep">▌</span>
              <span className="sf-comm__enc">ENC: AES-256</span>
              <span className="sf-comm__live">
                <span className="sf-dossier__livedot"></span>
                LIVE
              </span>
            </div>

            <header className="sf-comm__title-block">
              <h1 className="sf-comm__h1">
                <span className="bg-clip-text text-transparent">{t('form.title')}</span>
              </h1>
              <p className="sf-comm__subtitle">
                <span className="sf-bracket">[</span>
                <span>{t('form.subtitle')}</span>
                <span className="sf-bracket">]</span>
              </p>
            </header>

            <div className="sf-comm__body">
              <section className="sf-comm__col sf-comm__col--form">
                <div className="sf-dossier__section-label">[ DIRECT_MESSAGE ]</div>
                <Form />
              </section>

              <section className="sf-comm__col sf-comm__col--side">
                <div className="sf-dossier__section-label">[ ALT_CHANNELS ]</div>
                <ul className="sf-comm__channels">
                  {altChannels.map(({ code, label, handle, href, Icon }) => (
                    <li key={code}>
                      <a
                        href={href}
                        target={href.startsWith('mailto:') ? undefined : '_blank'}
                        rel={href.startsWith('mailto:') ? undefined : 'noopener noreferrer'}
                        className="sf-comm__channel-item group"
                      >
                        <span className="sf-comm__channel-code">{code}</span>
                        {Icon && <span className="sf-comm__channel-icon"><Icon /></span>}
                        <span className="sf-comm__channel-text">
                          <span className="sf-comm__channel-label">{label}</span>
                          <span className="sf-comm__channel-handle">{handle}</span>
                        </span>
                        <FaArrowRight className="sf-comm__channel-arrow" size={12} />
                      </a>
                    </li>
                  ))}
                </ul>

                <div className="sf-comm__meta">
                  <div className="sf-comm__meta-row"><span>LATENCY</span><span>~24H</span></div>
                  <div className="sf-comm__meta-row"><span>UPLINK</span><span className="sf-comm__meta-strong">STABLE</span></div>
                  <div className="sf-comm__meta-row"><span>AVAILABLE</span><span>WORLDWIDE</span></div>
                </div>

                <div className="sf-dossier__section-label cx-label">[ {t('contact.helpTitle')} ]</div>
                <ul className="cx-services cx-services--modern">
                  {services.map(({ key, Icon }) => (
                    <li key={key} className={`cx-service cx-service--${key}`}>
                      <Icon aria-hidden="true" />
                      <span>{t(`hero.disciplines.${key}`)}</span>
                    </li>
                  ))}
                </ul>
              </section>
            </div>

            <div className="sf-comm__routing">
              <span>ROUTING ::</span>
              <span className="sf-comm__route-node">portfolio.motirebuma</span>
              <FaArrowRight size={10} />
              <span className="sf-comm__route-node">secure-relay</span>
              <FaArrowRight size={10} />
              <span className="sf-comm__route-node sf-comm__route-node--final">inbox</span>
            </div>

            <footer className="sf-comm__footer">
              <span className="sf-comm__footer-tick">▸</span>
              {t('contact.footer', { year })}
            </footer>
          </article>
        </div>
      </section>
    );
  }

  /* Neobrutalism — POSTCARD / TRANSMIT MAIL */
  return (
    <div id='contact' className="pt-24 sm:pt-28 lg:pt-32 pb-12 sm:pb-16 relative overflow-hidden px-4 sm:px-6">
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <div className="hidden sm:block absolute top-20 right-20 w-16 h-16 bg-blue-300 rounded-full mix-blend-multiply filter blur-sm opacity-40 animate-blob"></div>
        <div className="hidden sm:block absolute bottom-40 left-20 w-20 h-20 bg-purple-300 rounded-full mix-blend-multiply filter blur-sm opacity-40 animate-blob animation-delay-2000"></div>
        <div className="hidden md:block absolute top-1/2 left-10 w-12 h-12 bg-pink-300 rounded-full mix-blend-multiply filter blur-sm opacity-35 animate-blob animation-delay-4000"></div>
      </div>

      <div className="max-w-6xl mx-auto w-full relative">
        <article className="nb-comm">
          <div className="nb-comm__strip">
            <span className="nb-comm__stamp">📮 POSTAGE</span>
            <span>TRANSMIT_MAIL · CHANNEL OPEN</span>
            <span className="nb-comm__cancel">✓ DELIVERED</span>
          </div>


          <header className="nb-comm__title-block">
            <h1 className="nb-comm__h1">{t('form.title')}</h1>
            <p className="nb-comm__subtitle">✉&nbsp;&nbsp;{t('form.subtitle')}&nbsp;&nbsp;✉</p>
          </header>

          <div className="nb-comm__body">
            <section className="nb-comm__col nb-comm__col--form">
              <div className="nb-comm__section-label">▶ DIRECT MESSAGE ◀</div>
              <Form />
            </section>

            <section className="nb-comm__col nb-comm__col--side">
              <div className="nb-comm__section-label">★ ALT CHANNELS ★</div>
              <ul className="nb-comm__channels">
                {altChannels.map(({ code, label, handle, href, Icon }, i) => (
                  <li key={code} className={`nb-comm__channel-item nb-comm__channel-item--${i % 3}`}>
                    <a
                      href={href}
                      target={href.startsWith('mailto:') ? undefined : '_blank'}
                      rel={href.startsWith('mailto:') ? undefined : 'noopener noreferrer'}
                      className="nb-comm__channel-link"
                    >
                      <span className="nb-comm__channel-code">[{code}]</span>
                      {Icon && <span className="nb-comm__channel-icon"><Icon /></span>}
                      <span className="nb-comm__channel-text">
                        <span className="nb-comm__channel-label">{label}</span>
                        <span className="nb-comm__channel-handle">{handle}</span>
                      </span>
                      <FaArrowRight className="nb-comm__channel-arrow" size={14} />
                    </a>
                  </li>
                ))}
              </ul>

              <div className="nb-comm__postage">
                <div className="nb-comm__postage-row"><span>★ LATENCY</span><span>~24H</span></div>
                <div className="nb-comm__postage-row"><span>★ STATUS</span><span>OPEN</span></div>
                <div className="nb-comm__postage-row"><span>★ AVAILABLE</span><span>WORLDWIDE</span></div>
              </div>

              <div className="nb-comm__section-label cx-label">★ {t('contact.helpTitle').toUpperCase()} ★</div>
              <ul className="cx-services cx-services--classic">
                {services.map(({ key, Icon }) => (
                  <li key={key} className={`cx-service cx-service--${key}`}>
                    <Icon aria-hidden="true" />
                    <span>{t(`hero.disciplines.${key}`)}</span>
                  </li>
                ))}
              </ul>
            </section>
          </div>

          <footer className="nb-comm__footer">
            <span className="nb-comm__postmark">◉ POSTMARKED ◉</span>
            <p>{t('contact.footer', { year })}</p>
          </footer>
        </article>
      </div>
    </div>
  );
};

export default Contact;
